// Pure calculation functions for the free employment calculators.
// Great Britain law only. Each rule carries its ID from
// docs/calculator-rule-register.md, which gives the authority, inputs,
// exceptions and tests. Figures live in data/statutory-rates.js.
// Dates are UTC calendar dates (YYYY-MM-DD) so the browser's timezone can't
// shift them.

import {
  WEEK_PAY_CAPS,
  IRREGULAR_ACCRUAL,
  TERMINATION_TAX_FREE,
  CLASS_1A_RATES,
  currentMinimumWage,
  inForceOn,
} from "../data/statutory-rates";

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const parse = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCMonth() === m - 1 ? dt : null;
};

const DAY = 86400000;
const iso = (dt) => dt.toISOString().slice(0, 10);
const addDays = (dt, n) => new Date(dt.getTime() + n * DAY);

// Same calendar day n years later. 29 February maps to 28 February in
// non-leap years, so a year of service is only complete once that day arrives.
const addYears = (dt, n) => {
  const y = dt.getUTCFullYear() + n;
  const m = dt.getUTCMonth();
  const d = Math.min(dt.getUTCDate(), new Date(Date.UTC(y, m + 1, 0)).getUTCDate());
  return new Date(Date.UTC(y, m, d));
};

const addMonths = (dt, n) => {
  const total = dt.getUTCMonth() + n;
  const y = dt.getUTCFullYear() + Math.floor(total / 12);
  const m = ((total % 12) + 12) % 12;
  const d = Math.min(dt.getUTCDate(), new Date(Date.UTC(y, m + 1, 0)).getUTCDate());
  return new Date(Date.UTC(y, m, d));
};

// Age in complete years on a date (a birthday counts on the day itself).
export const ageOn = (dobIso, onIso) => {
  const dob = parse(dobIso);
  const on = parse(onIso);
  if (!dob || !on || on < dob) return null;
  let years = on.getUTCFullYear() - dob.getUTCFullYear();
  if (addYears(dob, years) > on) years -= 1;
  return Math.max(0, years);
};

// Complete years of service between two dates, both days counted. The period
// of continuous employment begins with the day the employee starts work and
// ends with the day it is measured to (ERA 1996 s211(1)), so someone who
// started on 8 April 2002 has one year's service on 7 April 2003 (Pacitti
// Jones v O'Brien [2005] CSIH 56). That is the same as complete years from
// the day before the start date.
export const completeYears = (startIso, endIso) => {
  const start = parse(startIso);
  const end = parse(endIso);
  if (!start || !end || end < start) return null;
  return ageOn(iso(addDays(start, -1)), endIso);
};

// One month's continuous employment, both days counted (ERA 1996 s211(1)).
const hasOneMonth = (start, end) => addMonths(addDays(start, -1), 1) <= end;

// ---------------------------------------------------------------------------
// NOT-86: statutory minimum notice, ERA 1996 s86
// ---------------------------------------------------------------------------

export function statutoryNoticeWeeks(startIso, noticeIso) {
  const start = parse(startIso);
  const notice = parse(noticeIso);
  if (!start || !notice || notice < start) return null;
  if (!hasOneMonth(start, notice)) return { employer: 0, employee: 0, years: 0 };
  const years = completeYears(startIso, noticeIso);
  // s86(1): 1 week under 2 years; then 1 week per complete year, max 12.
  // s86(2): an employee gives at least 1 week once employed 1 month.
  return { employer: years < 2 ? 1 : Math.min(years, 12), employee: 1, years };
}

// ---------------------------------------------------------------------------
// RED-145: relevant date, ERA 1996 s145(2), (5), (6)
// ---------------------------------------------------------------------------

// Where the employer gives less than s86 notice (or none, e.g. pay in lieu),
// the relevant date for ss155, 162(1) and 227(3) is extended to when s86
// notice given on the material date would have expired.
export function redundancyRelevantDate({ startDate, endDate, noticeGivenDate }) {
  const end = parse(endDate);
  if (!end) return null;
  const material = noticeGivenDate ? parse(noticeGivenDate) : end; // s145(6)
  if (!material || material > end) return null;
  const stat = statutoryNoticeWeeks(startDate, iso(material));
  if (!stat) return null;
  const deemed = addDays(material, stat.employer * 7);
  const extended = deemed > end;
  return {
    actualEnd: endDate,
    materialDate: iso(material),
    statutoryNoticeWeeks: stat.employer,
    relevantDate: extended ? iso(deemed) : endDate,
    extended,
  };
}

// ---------------------------------------------------------------------------
// RED-155 / RED-162 / RED-CAP: statutory redundancy pay
// ---------------------------------------------------------------------------

export function redundancyPay({ dateOfBirth, startDate, endDate, noticeGivenDate, weeklyPay }) {
  const dob = parse(dateOfBirth);
  const start = parse(startDate);
  const pay = Number(weeklyPay);
  if (!dob || !start || !parse(endDate)) return { error: "Enter the date of birth, start date and leaving date." };
  if (start > parse(endDate)) return { error: "The start date must be before the leaving date." };
  if (dob >= start) return { error: "Check the date of birth: it is after the start date." };
  if (!(pay >= 0)) return { error: "Enter weekly pay before tax." };

  const rel = redundancyRelevantDate({ startDate, endDate, noticeGivenDate });
  if (!rel) return { error: "The date notice was given must be on or before the leaving date." };

  const capRow = inForceOn(WEEK_PAY_CAPS, rel.relevantDate);
  if (!capRow) return { error: "This calculator covers redundancies with a relevant date from 6 April 2025." };

  const years = completeYears(startDate, rel.relevantDate);
  const base = { relevant: rel, yearsOfService: years, cap: capRow.amount, capSource: capRow.authority };

  // s155: two years' continuous employment ending with the relevant date.
  if (years < 2) return { ...base, eligible: false, total: 0 };

  // s162(2)-(3): count back up to 20 complete years from the relevant date.
  // A year "in which" the employee was not below 41 (or 22) is judged by
  // their age on the first day of that year. Counting back, a year ends on
  // the relevant date (or an earlier anniversary of it) and begins the day
  // after the date one year before.
  const relDt = parse(rel.relevantDate);
  const counted = Math.min(years, 20);
  const bands = { under22: 0, age22to40: 0, age41plus: 0 };
  let weeks = 0;
  for (let i = 0; i < counted; i += 1) {
    const age = ageOn(dateOfBirth, iso(addDays(addYears(relDt, -(i + 1)), 1)));
    if (age >= 41) { weeks += 1.5; bands.age41plus += 1; }
    else if (age >= 22) { weeks += 1; bands.age22to40 += 1; }
    else { weeks += 0.5; bands.under22 += 1; }
  }

  const cappedWeek = Math.min(pay, capRow.amount);
  const total = round2(weeks * cappedWeek);
  return {
    ...base,
    eligible: true,
    yearsCounted: counted,
    weeks,
    weeklyPayUsed: cappedWeek,
    capped: pay > capRow.amount,
    total,
    taxFree: total <= TERMINATION_TAX_FREE.amount,
    breakdown: bands,
  };
}

// ---------------------------------------------------------------------------
// NOT-87 / NOT-88 / NOT-89: notice period and pay during notice
// ---------------------------------------------------------------------------

export const NOTICE_SITUATIONS = [
  { id: "working", label: "Working, or on garden leave" },
  { id: "sick", label: "Off sick or injured" },
  { id: "holiday", label: "On holiday" },
  { id: "family", label: "On maternity, paternity, adoption or other family leave" },
  { id: "pilon", label: "Not working it: paid in lieu" },
];

export function noticeAssessment({
  startDate,
  noticeDate,
  givenBy = "employer",
  contractAmount,
  contractUnit = "weeks",
  normalHours = "yes",
  situation = "working",
  weeklyPay,
}) {
  const stat = statutoryNoticeWeeks(startDate, noticeDate);
  if (!stat) return { error: "Enter a start date and a notice date after it." };
  const given = parse(noticeDate);
  const pay = weeklyPay === "" || weeklyPay == null ? null : Number(weeklyPay);
  if (pay != null && !(pay >= 0)) return { error: "Weekly pay must be zero or more." };

  const hasContract = contractAmount !== "" && contractAmount != null;
  const amount = hasContract ? Number(contractAmount) : null;
  if (hasContract && !(amount >= 0)) return { error: "Contractual notice must be zero or more." };

  const statutoryWeeks = givenBy === "employee" ? stat.employee : stat.employer;
  const statutoryEnd = addDays(given, statutoryWeeks * 7);
  const contractEnd = hasContract
    ? contractUnit === "months" ? addMonths(given, amount) : addDays(given, Math.round(amount * 7))
    : null;
  // s86(3)-(4): a contract can give longer notice, never shorter.
  const noticeEnd = contractEnd && contractEnd > statutoryEnd ? contractEnd : statutoryEnd;
  const contractShorter = contractEnd != null && contractEnd < statutoryEnd;

  // s87(4): ss87-91 don't apply where the notice the EMPLOYER must give under
  // the contract is at least a week more than s86(1). We only know the
  // employer's contractual notice when the employer is the one giving it.
  const employerStatEnd = addDays(given, stat.employer * 7);
  const s874 = givenBy === "employer" && contractEnd != null && contractEnd >= addDays(employerStatEnd, 7);

  // Which rule governs pay during the statutory notice period.
  let payRule;
  if (statutoryWeeks === 0) payRule = "none";
  else if (situation === "working" || situation === "pilon") payRule = "contract";
  else if (s874) payRule = "s874";
  else payRule = normalHours === "yes" ? "s88" : "s89";

  const weeksForEstimate = (noticeEnd - given) / DAY / 7;
  return {
    yearsOfService: stat.years,
    statutoryWeeks,
    statutoryEnd: iso(statutoryEnd),
    contractEnd: contractEnd ? iso(contractEnd) : null,
    noticeEnd: iso(noticeEnd),
    noticeWeeks: round2(weeksForEstimate),
    contractShorter,
    s874,
    payRule,
    givenBy,
    situation,
    // A basic-pay estimate only: a PILON clause may also cover benefits,
    // pension contributions or allowances (NOT-PILON).
    basicEstimate: pay != null ? round2(pay * weeksForEstimate) : null,
  };
}

// ---------------------------------------------------------------------------
// NOT-END: the last day of a notice period
// ---------------------------------------------------------------------------

// Notice runs from the day after it is given, unless the contract or the
// notice says otherwise (West v Kneels Ltd [1987] ICR 146; Wang v University
// of Keele UKEAT/0223/10), so the last day is the notice date plus the
// period. Months use the corresponding date, or the last day of the month
// where there is none (Dodds v Walker [1981] 1 WLR 1027). Written notice is
// given when the employee reads it or has a reasonable chance to (Newcastle
// upon Tyne Hospitals NHS Foundation Trust v Haywood [2018] UKSC 22).
export const NOTICE_UNITS = ["days", "weeks", "months"];

export function noticeEndDate({ noticeDate, amount, unit = "weeks" }) {
  const given = parse(noticeDate);
  if (!given) return { error: "Enter the date notice was given." };
  if (amount === "" || amount == null) return { error: "Enter the length of the notice." };
  const n = Number(amount);
  if (!Number.isInteger(n) || n < 1) return { error: "Notice length must be a whole number of 1 or more." };
  if (!NOTICE_UNITS.includes(unit)) return { error: "Choose days, weeks or months." };
  const end = unit === "months" ? addMonths(given, n) : addDays(given, unit === "weeks" ? n * 7 : n);
  return {
    noticeDate: iso(given),
    firstDay: iso(addDays(given, 1)),
    lastDay: iso(end),
    monthEndAdjusted: unit === "months" && end.getUTCDate() !== given.getUTCDate(),
  };
}

// ITEPA 2003 s402D post-employment notice pay, daily version:
// PENP = ((BP x D) / P) - T, never below zero.
export function postEmploymentNoticePay({ basicPayLastPeriod, daysInLastPeriod, unworkedNoticeDays, alreadyTaxed = 0 }) {
  const bp = Number(basicPayLastPeriod);
  const p = Number(daysInLastPeriod);
  const d = Number(unworkedNoticeDays);
  const t = Number(alreadyTaxed) || 0;
  if (!(bp >= 0) || !(p > 0) || !(d >= 0)) return null;
  return round2(Math.max(0, (bp * d) / p - t));
}

// ---------------------------------------------------------------------------
// PKG: redundancy package and tax, ITEPA 2003 ss309, 401-404 and 402A-402E
// ---------------------------------------------------------------------------

const money = (v) => (v === "" || v == null ? 0 : Number(v));

// PKG-ELTD: earliest lawful termination date = last day of a period equal to
// the minimum notice, beginning at the end of the trigger date (s402E(7)).
// PKG-D / PKG-P: D and P for the PENP formula (s402D(3), (6) and (6A)).
export function penpInputs({ noticeGivenDate, lastDay, minNoticeAmount, minNoticeUnit = "weeks", payFrequency = "monthly" }) {
  const last = parse(lastDay);
  if (!last) return { error: "Enter the last day of employment." };
  const trigger = noticeGivenDate ? parse(noticeGivenDate) : last; // s402E(2)
  if (!trigger) return { error: "Check the date notice was given." };
  if (trigger > last) return { error: "Notice must be given on or before the last day of employment." };
  const amount = Number(minNoticeAmount);
  if (!Number.isInteger(amount) || amount < 0) return { error: "Minimum notice must be a whole number of 0 or more." };
  const eltd = minNoticeUnit === "months" ? addMonths(trigger, amount) : addDays(trigger, amount * 7);
  const days = eltd > last ? Math.round((eltd - last) / DAY) : 0; // s402E(6)
  let D = days;
  let P;
  let rule = "days";
  if (payFrequency === "monthly") {
    // s402D(6): monthly pay, contractual notice in whole months, and a
    // post-employment notice period of a whole number of months.
    let wholeMonths = null;
    if (minNoticeUnit === "months" && days > 0) {
      for (let k = 1; k <= amount; k += 1) if (iso(addMonths(last, k)) === iso(eltd)) wholeMonths = k;
    }
    if (wholeMonths != null) {
      D = wholeMonths;
      P = 1;
      rule = "months";
    } else {
      P = 30.42; // s402D(6A)
    }
  } else {
    P = payFrequency === "weekly" ? 7 : payFrequency === "fourweekly" ? 28 : null;
  }
  return { triggerDate: iso(trigger), earliestLawfulTermination: iso(eltd), D, P, rule, unworkedDays: days };
}

export function redundancyPackage({
  statutoryRedundancy,
  enhancedRedundancy,
  otherCompensation,
  pilon,
  finalPay,
  holidayPay,
  otherEarnings,
  noticeGivenDate,
  lastDay,
  minNoticeAmount,
  minNoticeUnit,
  payFrequency,
  daysInPayPeriod,
  basicPayLastPeriod,
}) {
  const amounts = { statutoryRedundancy, enhancedRedundancy, otherCompensation, pilon, finalPay, holidayPay, otherEarnings };
  for (const [k, v] of Object.entries(amounts)) {
    if (!(money(v) >= 0)) return { error: "Amounts must be zero or more." };
    amounts[k] = money(v);
  }
  const p = penpInputs({ noticeGivenDate, lastDay, minNoticeAmount, minNoticeUnit, payFrequency });
  if (p.error) return p;
  const P = p.P ?? Number(daysInPayPeriod);
  if (!(P > 0)) return { error: "Enter the number of days in the last pay period." };
  const bp = money(basicPayLastPeriod);
  if (!(bp >= 0)) return { error: "Basic pay must be zero or more." };

  // PKG-RTA: statutory redundancy pay is exempt under s309 and is not a
  // relevant termination award; SP1/94 non-statutory redundancy lump sums
  // and other non-contractual compensation are.
  const rta = amounts.enhancedRedundancy + amounts.otherCompensation;
  // NOT-PENP, capped at the relevant termination awards (EIM13880). T is the
  // PILON or other notice pay taxed as earnings; holiday pay and termination
  // bonuses are excluded from T (s402D(1)).
  const penpFormula = postEmploymentNoticePay({
    basicPayLastPeriod: bp,
    daysInLastPeriod: P,
    unworkedNoticeDays: p.D,
    alreadyTaxed: amounts.pilon,
  });
  const penp = round2(Math.min(penpFormula, rta));

  // PKG-403: the £30,000 threshold covers statutory redundancy pay and the
  // rest of the relevant termination awards (s403).
  const pool = amounts.statutoryRedundancy + rta - penp;
  const threshold = TERMINATION_TAX_FREE.amount;
  const taxableExcess = round2(Math.max(0, pool - threshold));
  const class1ARate = inForceOn(CLASS_1A_RATES, lastDay)?.rate ?? CLASS_1A_RATES[0].rate;

  const earnings = round2(amounts.finalPay + amounts.holidayPay + amounts.otherEarnings + amounts.pilon + penp);
  const taxFree = round2(pool - taxableExcess);
  return {
    ...p,
    P,
    rta: round2(rta),
    penpFormula,
    penp,
    earnings,
    taxFree,
    taxableExcess,
    class1ARate,
    employerClass1A: round2(taxableExcess * class1ARate),
    total: round2(earnings + taxFree + taxableExcess),
  };
}

// ---------------------------------------------------------------------------
// HOL-13: regular-hours entitlement, WTR 1998 regs 13 and 13A
// ---------------------------------------------------------------------------

export const statutoryDays = (daysPerWeek) => Math.min(Number(daysPerWeek) * 5.6, 28);

export function regularHolidayEntitlement({ daysPerWeek, hoursPerWeek }) {
  const days = Number(daysPerWeek);
  if (!(days > 0 && days <= 7)) return { error: "Days per week must be between 0.5 and 7." };
  const entitlementDays = round2(statutoryDays(days));
  // GOV.UK's practical method for hours: average day = weekly hours / days.
  const hours = Number(hoursPerWeek);
  const entitlementHours = hours > 0 ? round2(entitlementDays * (hours / days)) : null;
  return { entitlementDays, entitlementHours, capped: days * 5.6 > 28 };
}

// ---------------------------------------------------------------------------
// HOL-14: leaving part-way through a leave year, WTR 1998 reg 14
// ---------------------------------------------------------------------------

// Statutory leave due on leaving = (A x B) - C, with A the regs 13 + 13A
// entitlement, B the proportion of the leave year expired and C leave taken.
// No contract can pay less than this for statutory leave; contractual leave
// above it follows the contract. reg 15A (the first-year limit on how much
// may be TAKEN) plays no part in this calculation.
export function leaverHoliday({
  daysPerWeek,
  contractualDays,
  leaveYearStart,
  startDate,
  endDate,
  daysTaken = 0,
  carriedOver = 0,
}) {
  const days = Number(daysPerWeek);
  if (!(days > 0 && days <= 7)) return { error: "Days per week must be between 0.5 and 7." };
  const ly = parse(leaveYearStart);
  const to = parse(endDate);
  if (!ly) return { error: "Enter the date your leave year starts." };
  if (!to) return { error: "Enter the leaving date." };
  const lyEnd = addDays(addYears(ly, 1), -1);
  const from = startDate ? parse(startDate) : ly;
  if (!from) return { error: "Check the start date." };
  if (from < ly || to > lyEnd || to < from) {
    return { error: "Start and leaving dates must fall inside the same leave year." };
  }
  const yearDays = Math.round((lyEnd - ly) / DAY) + 1;
  const fraction = (Math.round((to - from) / DAY) + 1) / yearDays;
  const statutoryAnnual = statutoryDays(days);
  const contractAnnual = contractualDays === "" || contractualDays == null ? statutoryAnnual : Math.max(Number(contractualDays), statutoryAnnual);
  const taken = Number(daysTaken) || 0;
  const carried = Number(carriedOver) || 0;
  if (taken < 0 || carried < 0) return { error: "Leave taken and carried over must be zero or more." };

  const statutoryDue = statutoryAnnual * fraction;
  const contractualExtraDue = (contractAnnual - statutoryAnnual) * fraction;
  // Leave taken is set first against statutory leave, which is the order
  // that gives the worker no less than the statutory minimum.
  const statutoryOwed = statutoryDue + carried - taken;
  return {
    fraction,
    yearDays,
    statutoryAnnual: round2(statutoryAnnual),
    contractAnnual: round2(contractAnnual),
    statutoryDue: round2(statutoryDue),
    contractualExtraDue: round2(contractualExtraDue),
    carried,
    taken,
    statutoryOwed: round2(Math.max(0, statutoryOwed)),
    contractualOwed: round2(Math.max(0, contractualExtraDue + Math.min(0, statutoryOwed))),
    overtaken: round2(Math.max(0, -(statutoryOwed + contractualExtraDue))),
  };
}

// ---------------------------------------------------------------------------
// HOL-15B / HOL-16A: irregular-hours and part-year workers
// ---------------------------------------------------------------------------

// reg 15B(3): 12.07% of hours worked in each pay period; 15B(5): under 30
// minutes is disregarded, 30 minutes or more becomes a whole hour.
export function irregularHoursAccrual(hoursPerPeriod) {
  const periods = hoursPerPeriod.map(Number).filter((h) => Number.isFinite(h) && h >= 0);
  const rows = periods.map((h) => {
    const exact = h * IRREGULAR_ACCRUAL.rate;
    return { worked: h, exact: round4(exact), accrued: roundHalfHour(exact) };
  });
  return {
    rows,
    totalWorked: round2(periods.reduce((a, b) => a + b, 0)),
    totalAccrued: rows.reduce((a, r) => a + r.accrued, 0),
  };
}

export const roundHalfHour = (hours) => {
  const whole = Math.floor(hours);
  return hours - whole >= 0.5 - 1e-9 ? whole + 1 : whole;
};

// reg 16A: rolled-up holiday pay is 12.07% of the pay for work done in the
// period. During sickness or statutory leave it is NOT this figure (see the
// regulation's own averaging rule), so callers only use this for work periods.
export const rolledUpHolidayPay = (periodPay) => {
  const pay = Number(periodPay);
  return pay >= 0 ? round2(pay * IRREGULAR_ACCRUAL.rate) : null;
};

// ---------------------------------------------------------------------------
// PRO-1 / PRO-APP: pro-rata salary, Part-time Workers Regulations 2000
// ---------------------------------------------------------------------------

export function proRataSalary({ fullTimeSalary, fullTimeHours, partTimeHours, ageBand = "21plus" }) {
  const fte = Number(fullTimeSalary);
  const ft = Number(fullTimeHours);
  const pt = Number(partTimeHours);
  if (!(fte > 0)) return { error: "Enter the full-time salary." };
  if (!(ft > 0)) return { error: "Enter full-time weekly hours." };
  if (!(pt > 0)) return { error: "Enter the part-time weekly hours." };
  const annual = round2(fte * (pt / ft));
  const hourly = round2(annual / (pt * 52));
  const nmw = currentMinimumWage();
  const band = nmw.bands.find((b) => b.id === ageBand) || nmw.bands[0];
  return {
    fraction: pt / ft,
    annual,
    monthly: round2(annual / 12),
    weekly: round2(annual / 52),
    hourly,
    minimumRate: band.rate,
    minimumLabel: band.label,
    // Compare before rounding, so £12.706 an hour is not shown as meeting £12.71.
    belowMinimum: annual / (pt * 52) < band.rate,
    moreThanFullTime: pt > ft,
  };
}

// Pay for part of a month. The contract's own formula comes first; where it
// says nothing, the Apportionment Act 1870 s2 treats annual salary as accruing
// from day to day, so each calendar day is 1/365 (1/366 in a leap year) of the
// annual salary (Hartley v King Edward VI College [2017] UKSC 39). Methods:
// "default" (that rule), "calendar" (contract: days in the month), "working"
// (contract: working days in the month).
export function partMonthPay({ annualSalary, firstDay, lastDay, method = "default", workingDays = [1, 2, 3, 4, 5] }) {
  const salary = Number(annualSalary);
  const from = parse(firstDay);
  const to = parse(lastDay);
  if (!(salary > 0)) return { error: "Enter the annual salary." };
  if (!from || !to || to < from) return { error: "Enter a first and last day, in order." };
  if (from.getUTCFullYear() !== to.getUTCFullYear() || from.getUTCMonth() !== to.getUTCMonth()) {
    return { error: "Both days must be in the same month." };
  }
  const y = from.getUTCFullYear();
  const m = from.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  const monthly = salary / 12;
  const calendarDays = to.getUTCDate() - from.getUTCDate() + 1;
  if (method === "working") {
    let inMonth = 0;
    let inPeriod = 0;
    for (let d = 1; d <= daysInMonth; d += 1) {
      if (!workingDays.includes(new Date(Date.UTC(y, m, d)).getUTCDay())) continue;
      inMonth += 1;
      if (d >= from.getUTCDate() && d <= to.getUTCDate()) inPeriod += 1;
    }
    return { method, monthly: round2(monthly), counted: inPeriod, outOf: inMonth, amount: inMonth ? round2(monthly * (inPeriod / inMonth)) : 0 };
  }
  if (method === "calendar") {
    return { method, monthly: round2(monthly), counted: calendarDays, outOf: daysInMonth, amount: round2(monthly * (calendarDays / daysInMonth)) };
  }
  const daysInYear = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 366 : 365;
  return { method: "default", monthly: round2(monthly), counted: calendarDays, outOf: daysInYear, amount: round2(salary * (calendarDays / daysInYear)) };
}

// ---------------------------------------------------------------------------

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
const round4 = (n) => Math.round((n + Number.EPSILON) * 10000) / 10000;

export const gbp = (n) =>
  Number(n).toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 });

export const longDate = (isoDate) =>
  parse(isoDate)?.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" }) ?? "";

export { TERMINATION_TAX_FREE, currentMinimumWage };
